import React from 'react';
import AdminLayout from '../../components/AdminLayout';
import AdminTable, { Column } from '../../components/AdminTable';
import { AdminBookingDto, fetchAdminReservations } from '@/features/adminPlatform/api';

const PAGE_SIZE = 20;

const formatDt = (s: string) => {
  if (!s) return '-';
  return new Date(s).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
};

const formatAmt = (n: number) =>
  new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(n);

const stateColor: Record<string, string> = {
  CONFIRMED: 'bg-green-100 text-green-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  COMPLETED: 'bg-blue-100 text-blue-800',
  CANCELED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-red-100 text-red-800',
  DRAFT: 'bg-gray-100 text-gray-700',
};

const AdminReservationsPage: React.FC = () => {
  const [rows, setRows] = React.useState<AdminBookingDto[]>([]);
  const [page, setPage] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAdminReservations(page, PAGE_SIZE)
      .then(res => {
        if (cancelled) return;
        setRows(res.content);
        setTotalPages(res.totalPages);
      })
      .catch(err => !cancelled && setError(err?.message ?? 'Chyba'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [page]);

  const columns: Column<AdminBookingDto>[] = [
    { key: 'code', header: 'Kód', render: r => <span className="font-mono text-xs">{r.code}</span> },
    { key: 'when', header: 'Začátek', render: r => formatDt(r.start) },
    { key: 'salon', header: 'Salon', render: r => r.salonName },
    { key: 'customer', header: 'Zákazník', render: r => r.customerName },
    { key: 'worker', header: 'Zaměstnanec', render: r => r.workerName ?? '—' },
    {
      key: 'state',
      header: 'Stav',
      render: r => (
        <span className={`px-2 py-0.5 rounded text-xs ${stateColor[r.state] ?? 'bg-gray-100 text-gray-700'}`}>{r.state}</span>
      ),
    },
    { key: 'amount', header: 'Částka', render: r => formatAmt(r.totalAmount) },
  ];

  return (
    <AdminLayout title="Rezervace">
      <AdminTable
        rows={rows}
        columns={columns}
        rowKey={r => r.id}
        loading={loading}
        error={error}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </AdminLayout>
  );
};

export default AdminReservationsPage;
